import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import {
	AbstractTranslatableAlertService,
	AllowedActions,
	CORE_ROUTE,
	CoreState,
	CurrentAction,
	CustomerComplianceService,
	KycMessageService,
	KycState,
	UiInteractionAction,
} from "@obg/ngx.core";
import { RouteAction } from "@obg/ngx.router";

import { AlertButtonColor } from "../alert/alert.model";
import { TrackingContext } from "../tracking/tracking.model";

import { KYC_VERIFY_POPUP_ALERT_ID } from "./kyc-message.const";

@Injectable()
export class MatKycMessageService implements KycMessageService {
	constructor(
		private store: Store<CoreState>,
		private uiInteractionAction: UiInteractionAction,
		private translatableAlertService: AbstractTranslatableAlertService,
		private customerComplianceService: CustomerComplianceService,
		private customerSupportService: CustomerSupportService
	) {}

	showMessage(status: CurrentAction) {
		const { action, currentStatus, isAllowed, jurisdiction } = status;

		if (isAllowed) {
			return;
		}

		let title: string;
		let message: string;

		switch (action) {
			case AllowedActions.Deposit:
				title = currentStatus === KycState.Requested ? "general.deposit" : "payment.deposit-not-allowed-title";
				message = "payment.deposit-not-allowed-message";
				break;
			case AllowedActions.Withdraw:
				title =
					currentStatus === KycState.Requested ? "general.withdraw" : "payment.withdraw-not-allowed-title";
				message = "payment.withdraw-not-allowed-message";
				break;
			default:
				if (currentStatus === KycState.Requested) {
					title = "kyc-verify-popup.title";
					message = "kyc-verify-popup.message";
				} else {
					title = "general.error";
					message = "general.action-not-allowed";
				}
		}

		const isDutchKycRequested = this.customerComplianceService.evaluateIsDutchKycRequested(
			status.complianceType,
			currentStatus
		);

		if (isDutchKycRequested) {
			title = "kyc-verify-popup.title";
			message = "kyc-verify-popup.verify-completion";
		}

		this.store.dispatch(
			this.uiInteractionAction.emit({
				context: TrackingContext.VerificationInteractionContext,
				event: GtmEventAction.VerifyAccountOpen,
				data: { RegistrationType: jurisdiction },
			})
		);

		this.translatableAlertService.open(
			{
				id: KYC_VERIFY_POPUP_ALERT_ID,
				title,
				message,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
					closeOnNavigation: false,
				},
				mustBeInteracted: true,
				buttons: [
					{
						color: AlertButtonColor.Primary,
						text: "general.cancel",
						handler: () => {
							this.store.dispatch(
								this.uiInteractionAction.emit({
									context: TrackingContext.VerificationInteractionContext,
									event: GtmEventAction.VerifyAccountClick,
									data: {
										ItemClicked: "Cancel",
										RegistrationType: jurisdiction,
									},
								})
							);
						},
					},
					{
						text: isDutchKycRequested ? "kyc-verify-popup.verify" : "kyc-verify-popup.support",
						handler: () => {
							if (isDutchKycRequested) {
								this.store.dispatch(RouteAction.navigate(CORE_ROUTE.verification.root));
								this.store.dispatch(
									this.uiInteractionAction.emit({
										context: TrackingContext.VerificationInteractionContext,
										event: GtmEventAction.VerifyAccountClick,
										data: {
											ItemClicked: "UploadDocuments",
											RegistrationType: jurisdiction,
										},
									})
								);
							} else {
								this.customerSupportService.openSupportPageInNewTab();
							}
						},
					},
				],
			},
			[]
		);
	}
}
